import {
  Form,
  LargeInput,
  ValidationContainer,
  ValidationMessage,
} from "@tandemdiabetes/sugarmate-react-components"
import escapeStringRegexp from "escape-string-regexp"
import {
  useCallback,
  useMemo,
  useState,
  useRef,
} from "react"
import type {
  ChangeEventHandler,
  FormEvent,
} from "react"

import type { AccountFormProps } from "../AccountFormProps"
import sharedStyles from "../AccountForms.module.scss"
import styles from "./EmailVerificationForm.module.scss"
import { useAutoSendEmailConfirmationCode } from "./useAutoSendEmailConfirmationCode"
import { useAutoValidateEmailConfirmationCode } from "./useAutoValidateEmailConfirmationCode"
import { useOnSubmitEmailVerificationCode } from "./useOnSubmitEmailVerificationCode"
import { useResendEmailConfirmationCode } from "./useResendEmailConfirmationCode"

import { usePolyglot } from "src/contexts"

const polyglotPrefix = "components.account_forms.email_confirmation."

const confirmationCodeId = "email-confirmation-code-id"
const confirmationCodeErrorId = "email-confirmation-code-error-id"

interface EmailVerification {
  emailAddress: string;
  emailConfirmationCode: string;
  emailConfirmationCodeValidForEmailAddress: string;
  hasSentEmailConfirmationCodeToEmailAddress: string;
}

interface Props extends AccountFormProps<EmailVerification> {
  // email verification requires an onSubmit function where the FormEvent is optional
  onSubmit: (ev?: FormEvent<HTMLFormElement>) => void;
}

export function EmailVerificationForm(props: Props) {
  const {
    children,
    onSubmit,
    form,
    updateForm,
  } = props

  const inputRef = useRef<HTMLInputElement>(null)
  const hadConfirmationCodeOnFirstRender = useRef<boolean>(!!form.emailConfirmationCode)
  const polyglot = usePolyglot()

  /*
   * this state keeps track of the code that the server verifies as invalid
   * so that we can show an inline error message to the user
   */
  const [
    invalidCode,
    setInvalidCode,
  ] = useState("")

  // the regex pattern the input must match
  const pattern = useMemo(
    () => {
      if (!invalidCode) {
        return "\\d{6}"
      }

      // show an error for the most recent code that failed validation
      return `(?!${escapeStringRegexp(invalidCode)})\\d{6}`
    },
    [invalidCode],
  )

  const onSendEmailSuccess = useCallback(
    (emailAddress: string) => {
      setInvalidCode("")
      updateForm({
        emailConfirmationCodeValidForEmailAddress: "",
        hasSentEmailConfirmationCodeToEmailAddress: emailAddress,
      })
    },
    [updateForm],
  )

  // automatically request server to send confirmation code to email
  useAutoSendEmailConfirmationCode({
    skipAutoSend: form.hasSentEmailConfirmationCodeToEmailAddress === form.emailAddress,
    emailAddress: form.emailAddress,
    language: polyglot.locale(),
    onSuccessCallback: onSendEmailSuccess,
  })

  // callback for resending confirmation code to email
  const resendEmailConfirmationCode = useResendEmailConfirmationCode({
    emailAddress: form.emailAddress,
    language: polyglot.locale(),
    onSuccessCallback: onSendEmailSuccess,
  })

  // on success save the email for which the code was validated and execute onSubmit
  const onValidateCodeSuccess = useCallback(
    (emailAddress: string, code: string) => {
      // only proceed if the user has not edited the code
      if (inputRef.current?.value === code) {
        updateForm({
          emailConfirmationCode: code,
          emailConfirmationCodeValidForEmailAddress: emailAddress,
        })

        onSubmit()
      }
    },
    [
      updateForm,
      onSubmit,
    ],
  )

  // callback for automatically validating confirmation code on change
  const autoValidateEmailConfirmationCode = useAutoValidateEmailConfirmationCode({
    emailAddress: form.emailAddress,
    onSuccessCallback: onValidateCodeSuccess,
  })

  const wrappedOnSubmit = useOnSubmitEmailVerificationCode({
    emailAddress: form.emailAddress,
    emailConfirmationCode: form.emailConfirmationCode,
    onSuccessCallback: onValidateCodeSuccess,
    onInvalidCallback: setInvalidCode,
  })

  const onCodeChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (ev) => {
      updateForm({
        emailConfirmationCode: ev.target.value,
        // any time the code changes clear this field to require revalidation
        emailConfirmationCodeValidForEmailAddress: "",
      })

      if (ev.target.checkValidity() && autoValidateEmailConfirmationCode) {
        void autoValidateEmailConfirmationCode(ev.target.value)
      }
    },
    [
      updateForm,
      autoValidateEmailConfirmationCode,
    ],
  )

  // skip validation if the code has already been validated for the current email address
  const skipValidation = form.emailConfirmationCodeValidForEmailAddress === form.emailAddress

  return (
    <Form
      className={sharedStyles.form}
      onSubmit={skipValidation ? onSubmit : wrappedOnSubmit}
    >
      <div
        className={sharedStyles.heading}
        id="email-verification-heading"
      >
        {polyglot.t(`${polyglotPrefix}title`)}
      </div>
      <div className={styles.descriptionGroup}>
        <div className={sharedStyles.description}>
          {polyglot.t(`${polyglotPrefix}description_one`)}
        </div>
        <div className={sharedStyles.description}>
          {polyglot.t(`${polyglotPrefix}description_two`)}
        </div>
      </div>
      <div className={sharedStyles.inputs}>
        <ValidationContainer
          validationMode={hadConfirmationCodeOnFirstRender.current ? "immediate" : "onFirstInteraction"}
        >
          <LargeInput
            ref={inputRef}
            id={confirmationCodeId}
            placeholder={polyglot.t(`${polyglotPrefix}code.placeholder`)}
            className={sharedStyles.input}
            type="text"
            value={form.emailConfirmationCode}
            onChange={onCodeChange}
            autoComplete="one-time-code"
            inputMode="numeric"
            maxLength={6}
            pattern={pattern}
            required
          />
          <ValidationMessage
            id={confirmationCodeErrorId}
            invalidMessage={{
              valueMissing: polyglot.t("validation_message.value_missing"),
              tooLong: polyglot.t(`${polyglotPrefix}code.too_long`),
              patternMismatch: polyglot.t(`${polyglotPrefix}code.incorrect`),
              customError: polyglot.t(`${polyglotPrefix}code.incorrect`),
            }}
          />
        </ValidationContainer>
      </div>
      {children}
      <button
        type="button"
        className={styles.resendButton}
        onClick={resendEmailConfirmationCode}
        disabled={!resendEmailConfirmationCode}
      >
        {polyglot.t(`${polyglotPrefix}resend_cta`)}
      </button>
    </Form>
  )
}
